import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NFTCard from "@/components/NFTCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Library, Plus, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CollectionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [coll, setColl] = useState(null);
  const [mine, setMine] = useState([]);
  const [picker, setPicker] = useState(false);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/collections/${id}`);
    setColl(data);
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!user) return;
    api.get("/assets/mine", { params: { tab: "catalog" } }).then(({ data }) => setMine(data));
  }, [user]);

  if (!coll) return <div className="py-20 grid place-items-center text-zinc-500"><Loader2 className="animate-spin" /></div>;
  const isOwner = user && user.id === coll.owner_id;

  const addSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/collections/${id}/assets`, { asset_ids: ids });
      toast.success(`Added ${ids.length} asset${ids.length > 1 ? "s" : ""}`);
      setPicker(false); setSelected({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <Link to="/collections" className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1">
        <ArrowLeft size={12} /> All collections
      </Link>

      <div className="mt-6 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-1">
          <div className="card-surface overflow-hidden">
            <div className="aspect-square bg-[#0A0A0A]">
              {coll.cover_path ? (
                <img src={fileUrl(coll.cover_path)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center bg-gradient-to-br from-violet-500/10 via-cyan8r/5 to-black">
                  <Library size={64} className="text-cyan8r/30" strokeWidth={1.1} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Collection</div>
          <h1 className="font-display text-5xl tracking-tighter mt-2">{coll.name}</h1>
          <Link to={`/profile/${coll.owner_id}`} className="text-sm text-zinc-400 hover:text-white mt-3 inline-block">
            curated by <span className="text-white">{coll.owner_name}</span> · {coll.school_name || "—"}
          </Link>
          {coll.description && <p className="text-zinc-400 mt-4 leading-relaxed">{coll.description}</p>}
          <div className="text-xs font-mono text-zinc-500 mt-6">{coll.asset_count} assets</div>

          {isOwner && (
            <Dialog open={picker} onOpenChange={setPicker}>
              <DialogTrigger asChild>
                <Button data-testid="add-to-collection-btn" className="mt-6 bg-white text-black hover:bg-zinc-200">
                  <Plus size={14} className="mr-1" /> Add assets
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-3xl">
                <DialogHeader><DialogTitle>Add assets to "{coll.name}"</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-auto">
                  {mine.filter((a) => !(coll.asset_ids || []).includes(a.id)).map((a) => {
                    const isSel = !!selected[a.id];
                    const isImg = (a.file_type || "").startsWith("image/");
                    return (
                      <button
                        key={a.id}
                        onClick={() => setSelected({ ...selected, [a.id]: !isSel })}
                        data-testid={`pick-${a.id}`}
                        className={`relative text-left rounded-md overflow-hidden border ${isSel ? "border-cyan8r" : "border-white/10"} bg-[#0A0A0A]`}
                      >
                        <div className="aspect-square bg-black grid place-items-center">
                          {isImg ? <img src={fileUrl(a.file_path)} alt="" className="w-full h-full object-cover" /> : <div className="text-xs text-zinc-500">{a.title}</div>}
                        </div>
                        <div className="p-2 text-xs truncate">{a.title}</div>
                        {isSel && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-cyan8r text-black grid place-items-center text-[10px]">✓</div>}
                      </button>
                    );
                  })}
                  {mine.length === 0 && (
                    <div className="col-span-full text-center text-xs text-zinc-500 py-8">Mint and approve some assets first.</div>
                  )}
                </div>
                <Button onClick={addSelected} disabled={busy} className="w-full bg-white text-black hover:bg-zinc-200 mt-3">
                  {busy ? "Adding…" : `Add ${Object.values(selected).filter(Boolean).length} asset(s)`}
                </Button>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="mt-12">
        {(coll.assets || []).length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-xl py-20 text-center text-zinc-500">No assets in this collection yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {coll.assets.map((a) => <NFTCard key={a.id} asset={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}
